import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, CalendarIcon } from '@utils/icons';
import { DateField } from '@components/ui/DateField/DateField';
import type { IProductivityCustomRange, IProductivityTimeframe } from '@interfaces/IProductivity';
import styles from './TimeframeSelector.module.css';

const OPTIONS: IProductivityTimeframe[] = ['thisWeek', 'thisMonth', 'thisYear', 'custom'];

interface ITimeframeSelectorProps {
  value: IProductivityTimeframe;
  onChange: (value: IProductivityTimeframe) => void;
  customRange: IProductivityCustomRange;
  onCustomRangeChange: (range: IProductivityCustomRange) => void;
}

export function TimeframeSelector({ value, onChange, customRange, onCustomRangeChange }: ITimeframeSelectorProps) {
  const { t, i18n } = useTranslation();

  // No `timeZone` on purpose (unlike DateField): the picker hands over
  // browser-local midnight, so formatting through a zone would shift the label
  // a day away from the range actually queried.
  const dateFormat = new Intl.DateTimeFormat(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const showingText = (() => {
    if (value !== 'custom') return t(`productivity.timeframe.${value}`);
    if (customRange.from && customRange.to)
      return `${dateFormat.format(customRange.from)} → ${dateFormat.format(customRange.to)}`;
    return t('productivity.timeframe.customPickDates');
  })();

  return (
    <div className={styles.bar}>
      <div className={styles.left}>
        <span className={styles.label}>
          <CalendarIcon size={14} />
          {t('productivity.timeframe.label')}
        </span>

        <div className={styles.options} role="group" aria-label={t('productivity.timeframe.label')}>
          {OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`${styles.option} ${value === opt ? styles.active : ''}`}
              onClick={() => onChange(opt)}
              aria-pressed={value === opt}
            >
              {t(`productivity.timeframe.${opt}`)}
            </button>
          ))}
        </div>

        {value === 'custom' && (
          <div className={styles.rangePicker}>
            <div className={styles.dateFieldWrap}>
              <DateField
                value={customRange.from}
                onChange={(date) => onCustomRangeChange({ ...customRange, from: date })}
                placeholder={t('productivity.timeframe.from')}
                ariaLabel={t('productivity.timeframe.from')}
                maxDate={customRange.to ?? undefined}
              />
            </div>
            <span className={styles.rangeArrow}><ArrowRightIcon size={14} /></span>
            <div className={styles.dateFieldWrap}>
              <DateField
                value={customRange.to}
                onChange={(date) => onCustomRangeChange({ ...customRange, to: date })}
                placeholder={t('productivity.timeframe.to')}
                ariaLabel={t('productivity.timeframe.to')}
                minDate={customRange.from ?? undefined}
              />
            </div>
          </div>
        )}
      </div>

      <span className={styles.showing}>
        {t('productivity.timeframe.showing')}{' '}
        <strong>{showingText}</strong>
      </span>
    </div>
  );
}